import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import TexturePattern from './TexturePattern';
import api from '../services/api';
import { 
  FaHome, FaBuilding, FaNewspaper, FaFileAlt, FaSearch, FaUsers, FaCog,
  FaBullseye, FaEye, FaUserFriends, FaHistory, FaMonument,
  FaList, FaGavel, FaFileContract, FaBook, FaBalanceScale, FaClipboardList,
  FaDollarSign, FaHandshake, FaChartLine, FaFileInvoiceDollar, FaShieldAlt,
  FaFileSignature, FaSitemap, FaProjectDiagram, FaGavel as FaLaw, FaUserCog,
  FaClipboardCheck, FaLandmark, FaUser, FaImages, FaCalendarAlt, FaComments, FaEnvelope,
  FaCheckCircle, FaTimes, FaBars, FaClock, FaUserTie, FaMapMarkerAlt, FaChevronDown, FaChevronUp,
  FaCross, FaPrayingHands, FaMicrophone, FaHeart, FaCalendarCheck,
  FaFacebookF, FaInstagram, FaChurch, FaStore, FaAddressBook, FaIdCard,
  FaTv, FaBroadcastTower, FaGlobe, FaUserGraduate, FaChild, FaSchool,
  FaHandsHelping, FaUsersCog, FaBriefcase, FaPrint
} from 'react-icons/fa';
import { SiTiktok } from 'react-icons/si';
import './Header.css';
import '../styles/MOBILE-UX-2026.css';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileActiveSubmenu, setMobileActiveSubmenu] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const menuRef = useRef(null);
  const menuItemRefs = useRef([]);
  const lastScrollYRef = useRef(0);

  const { data: casasList = [] } = useQuery({
    queryKey: ['casas-encuentro-menu'],
    queryFn: async () => {
      try {
        const res = await api.get('/casas-encuentro');
        return res.data || [];
      } catch {
        return [];
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: config = {} } = useQuery({
    queryKey: ['configuracion'],
    queryFn: async () => {
      try {
        const res = await api.get('/configuracion');
        return res.data || {};
      } catch {
        return {};
      }
    },
    retry: false,
    staleTime: 10 * 60 * 1000,
  });

  const SIID_PATH = '/search-profile';
  const SIID_TITLE = 'Sistema Integrado de Información Diocesana — búsqueda de perfiles';

  const LOGO_INTRINSIC_WIDTH = 1465;
  const LOGO_INTRINSIC_HEIGHT = 713;
  const logoSrcNuevo = `${process.env.PUBLIC_URL || ''}/images/escudo_letras_nuevo.png`;
  const logoSrcFallback = `${process.env.PUBLIC_URL || ''}/images/escudo_letras.png`;

  const FACEBOOK_URL_DIOCESIS = 'https://www.facebook.com/Diocesisdeipialesoficial/';
  const facebookUrl = (config.facebook_url && !config.facebook_url.includes('Guachucal')) ? config.facebook_url : FACEBOOK_URL_DIOCESIS;
  const instagramUrl = config.instagram_url || '#';
  const tiktokUrl = config.tiktok_url || '#';


  // Ajustar automáticamente el offset del header fixed (evita huecos o solapamientos)
  useLayoutEffect(() => {
    const headerEl = menuRef.current;
    if (!headerEl || typeof document === 'undefined') return;

    const updateOffset = () => {
      const h = Math.ceil(headerEl.getBoundingClientRect().height || 0);
      if (h > 0) document.documentElement.style.setProperty('--header-offset', `${h}px`);
    };

    updateOffset();

    let ro;
    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => updateOffset());
      ro.observe(headerEl);
    }
    window.addEventListener('resize', updateOffset);
    return () => {
      window.removeEventListener('resize', updateOffset);
      if (ro) ro.disconnect();
    };
  }, []);

  // Estructura del menú según diseño: Inicio, Directorio, Pastoral, Comunicaciones, Parroquias
  const menuItems = [
    {
      label: 'Inicio',
      path: '/',
      hasSubmenu: true,
      icon: FaHome,
      description: 'Conoce la Diócesis',
      submenu: [
        { label: 'Página de inicio', path: '/', icon: FaHome },
        { label: 'Historia', path: '/historia', icon: FaHistory },
        { label: 'Obispos Diócesis de Ipiales', path: '/acerca#autoridades', icon: FaUserTie },
        { label: 'Nuestro Obispo', path: '/acerca#estructura', icon: FaUserTie },
        { label: 'Misión', path: '/acerca#mision', icon: FaBullseye },
        { label: 'Visión', path: '/acerca#vision', icon: FaEye },
        { label: 'Insignias', path: '/acerca', icon: FaLandmark }
      ]
    },
    {
      label: 'Directorio',
      path: '/servicios',
      hasSubmenu: true,
      icon: FaAddressBook,
      description: 'Curia, medios y servicios',
      submenuGroups: true,
      submenu: [
        {
          groupTitle: 'Curia diocesana',
          items: [
            { label: 'Vicaría General', path: '/directorio/vicaria-general', icon: FaUserCog },
            { label: 'Secretaría General', path: '/directorio/secretaria-general', icon: FaBuilding },
            { label: 'Tesorería', path: '/directorio/tesoreria', icon: FaDollarSign },
            { label: 'Vicaría para la pastoral', path: '/directorio/vicaria-pastoral', icon: FaPrayingHands },
            { label: 'Cancillería', path: '/directorio/cancilleria', icon: FaFileSignature },
            { label: 'Tribunal Eclesiástico', path: '/directorio/tribunal-eclesiastico', icon: FaBalanceScale },
            { label: 'Clero diocesano', path: '/clero', icon: FaUsers },
            {
              label: 'SIID — búsqueda de perfiles',
              path: SIID_PATH,
              icon: FaIdCard,
              openInNewTab: true,
              title: SIID_TITLE
            }
          ]
        },
        {
          groupTitle: 'Medios de comunicación',
          items: [
            { label: 'TV Ipiales', path: '/directorio/tv-ipiales', icon: FaTv },
            { label: 'Rumba 104.7 FM', path: '/directorio/rumba-stereo', icon: FaBroadcastTower },
            { label: 'Radio Las Lajas 103.9 FM', path: '/directorio/radio-lajas', icon: FaBroadcastTower }
          ]
        },
        {
          groupTitle: 'Soluciones de negocio',
          items: [
            { label: 'Almacén la Mestiza', path: '/directorio/almacen-mestiza', icon: FaStore },
            { label: 'La Imprenta', path: '/directorio/imprenta', icon: FaPrint }
          ]
        }
      ]
    },
    {
      label: 'Pastoral',
      path: '/pastoral',
      hasSubmenu: true,
      icon: FaPrayingHands,
      description: 'Centros y comisiones de la acción pastoral',
      submenuGroups: true,
      submenu: [
        {
          groupTitle: 'Centro pastoral para la evangelización y la fe',
          items: [
            { label: 'Proceso Diocesano (mapa)', path: '/pastoral/proceso-diocesano', icon: FaMapMarkerAlt },
            { label: 'Pastoral de Multitudes', path: '/pastoral/pastoral-multitudes', icon: FaUsers },
            { label: 'Pastoral de Catequesis', path: '/pastoral/pastoral-catequesis', icon: FaBook },
            { label: 'Pastoral de Formación de Laicos', path: '/pastoral/pastoral-formacion-laicos', icon: FaUserGraduate },
            { label: 'Pastoral Bíblica: Animación Bíblica de la Pastoral', path: '/pastoral/pastoral-biblica', icon: FaBook },
            { label: 'Pastoral Misionera: Obras Misionales Pontificias', path: '/pastoral/pastoral-misionera', icon: FaGlobe },
            { label: 'Pastoral Litúrgica', path: '/pastoral/pastoral-liturgica', icon: FaCross }
          ]
        },
        {
          groupTitle: 'Centro pastoral para los estados de vida',
          items: [
            { label: 'Pastoral de Vida Consagrada', path: '/pastoral/pastoral-vida-consagrada', icon: FaChurch },
            { label: 'Pastoral Presbiteral', path: '/pastoral/pastoral-presbiteral', icon: FaUserTie },
            { label: 'Pastoral Vocacional y del Seminario', path: '/pastoral/pastoral-vocacional', icon: FaChurch }
          ]
        },
        {
          groupTitle: 'Centro para la comunión y la participación',
          items: [
            { label: 'Pastoral Grupos de Familia', path: '/pastoral/pastoral-grupos-familia', icon: FaUsers },
            { label: 'Pastoral Familiar', path: '/pastoral/pastoral-familiar', icon: FaHeart },
            { label: 'Pastoral Infantil', path: '/pastoral/pastoral-infantil', icon: FaChild },
            { label: 'Pastoral Juvenil', path: '/pastoral/pastoral-juvenil', icon: FaUsers },
            { label: 'Pastoral Educativa y Universitaria', path: '/pastoral/pastoral-educativa', icon: FaSchool },
            { label: 'Pastoral Indígena', path: '/pastoral/pastoral-indigena', icon: FaMapMarkerAlt },
            { label: 'Movimientos Apostólicos', path: '/pastoral/movimientos-apostolicos', icon: FaChurch },
            { label: 'Consejo Diocesano de Pastoral', path: '/pastoral/consejo-diocesano-pastoral', icon: FaUsersCog },
            { label: 'Equipo Diocesano de Animación Pastoral EDAP', path: '/pastoral/edap', icon: FaSitemap },
            { label: 'Acompañamiento: Equipos Parroquiales de Animación Pastoral E.P.A.P.', path: '/pastoral/epap', icon: FaHandsHelping },
            { label: 'Acompañamiento: Equipos de Coordinación Sectorial E.C.S.', path: '/pastoral/ecs', icon: FaHandsHelping },
            { label: 'Acompañamiento: Asamblea Pastoral Parroquial', path: '/pastoral/asamblea-pastoral', icon: FaHandsHelping },
            { label: 'Acompañamiento: Consejo Económico Parroquial (C.E.P)', path: '/pastoral/cep', icon: FaHandsHelping }
          ]
        },
        {
          groupTitle: 'Centro pastoral para relaciones y evangelización de lo social',
          items: [
            { label: 'Comisión Diocesana de Pastoral Social', path: '/pastoral/comision-pastoral-social', icon: FaHandsHelping },
            { label: 'Pastoral de Medios de Comunicación', path: '/pastoral/pastoral-medios', icon: FaTv },
            { label: 'Pastoral Penitenciaria', path: '/pastoral/pastoral-penitenciaria', icon: FaChurch }
          ]
        }
      ]
    },
    {
      label: 'Comunicaciones',
      path: '/noticias',
      hasSubmenu: true,
      icon: FaNewspaper,
      description: 'Noticias, decretos y políticas',
      submenuGroups: true,
      submenu: [
        { label: 'Comunicados oficiales', path: '/noticias', icon: FaFileAlt },
        { label: 'Decretos', path: '/gaceta', icon: FaGavel },
        { label: 'Noticias', path: '/noticias', icon: FaNewspaper },
        {
          groupTitle: 'Políticas',
          items: [
            { label: 'Política de privacidad', path: '/politica-privacidad', icon: FaFileContract },
            { label: 'Protección a Menores', path: '/proteccion-menores', icon: FaShieldAlt },
            { label: 'Tratamiento de datos', path: '/tratamiento-datos', icon: FaClipboardList }
          ]
        },
        { label: 'Galería fotográfica Destellos de Fe', path: '/galeria', icon: FaImages }
      ]
    },
    /* Parroquias: enlace directo (sin submenú), por eso no lleva flecha ▼ como los demás */
    {
      label: 'Parroquias',
      path: '/parroquias',
      hasSubmenu: false,
      icon: FaChurch
    },
    // Casas de encuentros: submenú desde API para que los slugs coincidan con la BD
    (() => {
      const submenuFromApi = Array.isArray(casasList) && casasList.length > 0
        ? casasList.map((c, i) => ({
            label: c.titulo || `Casa ${i + 1}`,
            path: `/casas-encuentro/${c.slug}`,
            icon: FaHome
          }))
        : [
            { label: 'Casa Pastoral de San Juan', path: '/casas-encuentro/casa-pastoral-san-juan', icon: FaHome },
            { label: 'Casa de encuentro El Capulí', path: '/casas-encuentro/casa-capuli', icon: FaHome }
          ];
      const firstPath = submenuFromApi[0]?.path || '/casas-encuentro/casa-pastoral-san-juan';
      return {
        label: 'Casas de encuentros',
        path: firstPath,
        hasSubmenu: true,
        icon: FaHome,
        description: 'Casa Pastoral y Casa de encuentro',
        submenu: submenuFromApi
      };
    })()
  ];

  const handleSubmenuClick = () => {
    setOpenDropdown(null);
    setFocusedIndex(null);
    setMobileMenuOpen(false);
    setMobileActiveSubmenu(null);
  };

  const handleNavClick = () => {
    setFocusedIndex(null);
    setMobileMenuOpen(false);
    setMobileActiveSubmenu(null);
  };

  /** Navegar y cerrar menú móvil: hacer navigate primero para asegurar que la ruta cambie */
  const handleMobileLinkClick = (path) => (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    setMobileActiveSubmenu(null);
    setOpenDropdown(null);
    navigate(path);
  };

  const handleMobileSubmenuToggle = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobileActiveSubmenu(mobileActiveSubmenu === index ? null : index);
  };

  // Navegación por teclado
  const handleKeyDown = (e, index, hasSubmenu) => {
    const menuItems = menuItemRefs.current;
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        if (index < menuItems.length - 1) {
          menuItems[index + 1]?.focus();
          setFocusedIndex(index + 1);
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (index > 0) {
          menuItems[index - 1]?.focus();
          setFocusedIndex(index - 1);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (hasSubmenu) setOpenDropdown(openDropdown === index ? null : index);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (hasSubmenu && openDropdown === index) setOpenDropdown(null);
        break;
      case 'Escape':
        e.preventDefault();
        setOpenDropdown(null);
        setFocusedIndex(null);
        menuItems[index]?.blur();
        break;
      case 'Enter':
      case ' ':
        if (hasSubmenu) {
          e.preventDefault();
          setOpenDropdown(openDropdown === index ? null : index);
        }
        break;
      default:
        break;
    }
  };

  const handleSubmenuKeyDown = (e, parentIndex) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        let next = e.target.closest('li')?.nextElementSibling;
        while (next && !next.querySelector('.nav-dropdown-link')) next = next.nextElementSibling;
        next?.querySelector('.nav-dropdown-link')?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        let prev = e.target.closest('li')?.previousElementSibling;
        while (prev && !prev.querySelector('.nav-dropdown-link')) prev = prev.previousElementSibling;
        if (prev) prev.querySelector('.nav-dropdown-link')?.focus();
        else menuItemRefs.current[parentIndex]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        menuItemRefs.current[parentIndex]?.focus();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileActiveSubmenu(null);
  }, [location.pathname]);

  // Prevenir scroll del body/html cuando el menú móvil está abierto
  useEffect(() => {
    if (mobileMenuOpen) {
      document.documentElement.classList.add('menu-open');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('menu-open');
    } else {
      document.documentElement.classList.remove('menu-open');
      document.body.style.overflow = '';
      document.body.classList.remove('menu-open');
    }
    return () => {
      document.documentElement.classList.remove('menu-open');
      document.body.style.overflow = '';
      document.body.classList.remove('menu-open');
    };
  }, [mobileMenuOpen]);

  // Compactar header al hacer scroll (escritorio) / ocultar al bajar (móvil)
  useEffect(() => {
    let rafId = null;

    const handleScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);

      rafId = requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const lastScrollY = lastScrollYRef.current;
        const isMobile = window.innerWidth <= 768;

        if (isMobile) {
          if (currentScrollY > lastScrollY && currentScrollY > 100) {
            setIsScrolled(true);
          } else if (currentScrollY < lastScrollY) {
            setIsScrolled(false);
          }
        } else {
          setIsScrolled(currentScrollY > 48);
        }

        lastScrollYRef.current = currentScrollY;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Cerrar dropdown al hacer clic fuera del header
  useEffect(() => {
    const handleDocClick = (e) => {
      if (openDropdown === null) return;
      const header = menuRef.current;
      if (header && !header.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener('click', handleDocClick, true);
    return () => document.removeEventListener('click', handleDocClick, true);
  }, [openDropdown]);

  return (
    <>
      {/* Overlay de fondo oscuro */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      {/* Menú móvil - Overlay superpuesto */}
      <ul className={`mobile-nav-menu ${mobileMenuOpen ? 'open' : ''}`}>
        {/* Cabecera del menú: Buscar (izq) + Cerrar (der) */}
        <li className="mobile-menu-close-header">
          <Link
            to="/busqueda"
            className="mobile-menu-search-btn"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Buscar"
            title="Buscar"
          >
            <FaSearch aria-hidden />
            <span>Buscar</span>
          </Link>
          <button
            type="button"
            className="mobile-menu-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Cerrar menú"
            title="Cerrar"
          >
            <FaTimes aria-hidden />
            <span>Cerrar</span>
          </button>
        </li>
        {menuItems.map((item, index) => (
          <li key={index} className={`mobile-nav-item ${mobileActiveSubmenu === index ? 'active-submenu' : ''}`}>
            {item.hasSubmenu ? (
              <>
                <button
                  className={`mobile-nav-link ${mobileActiveSubmenu === index ? 'active' : ''}`}
                  onClick={(e) => {
                    if (index === 0) navigate('/');
                    handleMobileSubmenuToggle(index, e);
                  }}
                >
                  {item.icon && <item.icon className="mobile-nav-icon" />}
                  <span>{item.label}</span>
                  <span className="mobile-nav-arrow">{mobileActiveSubmenu === index ? <FaChevronUp /> : <FaChevronDown />}</span>
                </button>
                <ul className="mobile-nav-submenu">
                  {item.submenu.map((subItem, subIndex) => {
                    if (subItem.groupTitle) {
                      return (
                        <React.Fragment key={subIndex}>
                          <li className="mobile-nav-group-title">{subItem.groupTitle}</li>
                          {subItem.items.map((it, i) => (
                            <li key={`${subIndex}-${i}`}>
                              {it.openInNewTab ? (
                                <a
                                  href={it.path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mobile-nav-sublink"
                                  title={it.title || it.label}
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  <span>{it.label}</span>
                                </a>
                              ) : (
                                <Link to={it.path} onClick={handleMobileLinkClick(it.path)} className="mobile-nav-sublink">
                                  <span>{it.label}</span>
                                </Link>
                              )}
                            </li>
                          ))}
                        </React.Fragment>
                      );
                    }
                    return (
                      <li key={subIndex}>
                        <Link to={subItem.path} onClick={handleMobileLinkClick(subItem.path)} className="mobile-nav-sublink">
                          <span>{subItem.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : item.openInNewTab ? (
              <a
                href={item.path}
                className="mobile-nav-link"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                title={item.title || item.label}
              >
                {item.icon && <item.icon className="mobile-nav-icon" />}
                <span>{item.label}</span>
              </a>
            ) : (
              <Link 
                to={item.path} 
                className={`mobile-nav-link${location.pathname === item.path ? ' active' : ''}`}
                onClick={handleMobileLinkClick(item.path)}
                style={{ textDecoration: 'none' }}
              >
                {item.icon && <item.icon className="mobile-nav-icon" />}
                <span style={{ textDecoration: 'none' }}>{item.label}</span>
              </Link>
            )}
          </li>
        ))}
        {/* Redes sociales al final del menú móvil (igual que menú accesibilidad) */}
        <li className="mobile-nav-social-wrapper">
          <div className="mobile-nav-social-separator" aria-hidden>Redes sociales</div>
          <div className="mobile-nav-social">
            <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="mobile-nav-social-link" aria-label="Facebook">
              <FaFacebookF />
            </a>
            <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="mobile-nav-social-link" aria-label="Instagram">
              <FaInstagram />
            </a>
            <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" className="mobile-nav-social-link" aria-label="TikTok">
              <SiTiktok />
            </a>
          </div>
        </li>
      </ul>
    <header className={`header ${isScrolled ? 'header-scrolled' : ''} ${openDropdown !== null ? 'mega-menu-open' : ''}`} ref={menuRef}>
      {/* Una sola franja: logo, menú, búsqueda/idioma flotantes */}
      <div className="header-top">
        <div className="header-container">
          <a
            href="/"
            className="header-logo"
            aria-current={location.pathname === '/' ? 'page' : undefined}
          >
            <img
              src={logoSrcNuevo}
              srcSet={`${logoSrcNuevo} ${LOGO_INTRINSIC_WIDTH}w`}
              sizes="(max-width: 360px) 280px, (max-width: 480px) 300px, (max-width: 968px) 320px, 340px"
              alt="Diócesis de Ipiales - En comunión, participación y misión"
              className="header-logo-img"
              width={LOGO_INTRINSIC_WIDTH}
              height={LOGO_INTRINSIC_HEIGHT}
              decoding="async"
              fetchPriority="high"
              onError={(e) => {
                const img = e.target;
                if (!img.src.includes('escudo_letras.png')) {
                  img.src = logoSrcFallback;
                  img.srcset = `${logoSrcFallback} 1536w`;
                  img.sizes = img.sizes || '(max-width: 360px) 240px, (max-width: 480px) 260px, (max-width: 968px) 280px, 300px';
                  img.width = 1536;
                  img.height = 1024;
                } else {
                  img.style.display = 'none';
                }
              }}
            />
          </a>
          
          {/* Botón hamburguesa solo para móvil - Movido al espacio superior */}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
          
          {/* Menú desktop: dropdown simple por clic (una lista debajo de cada ítem) */}
          <nav className="header-nav" aria-label="Menú principal">
            <ul className="nav-menu">
            {menuItems.map((item, index) => (
              <li
                key={index}
                className={`nav-item ${item.hasSubmenu ? 'has-submenu' : ''} ${location.pathname === item.path ? 'active' : ''} ${openDropdown === index ? 'dropdown-open' : ''}`}
              >
                {item.hasSubmenu ? (
                  <button
                    type="button"
                    className="nav-link nav-link-trigger"
                    ref={el => menuItemRefs.current[index] = el}
                    onClick={() => {
                      if (index === 0) {
                        navigate('/');
                      }
                      setOpenDropdown(openDropdown === index ? null : index);
                    }}
                    onKeyDown={(e) => handleKeyDown(e, index, true)}
                    onFocus={() => setFocusedIndex(index)}
                    aria-expanded={openDropdown === index}
                    aria-haspopup="true"
                  >
                    {item.icon && <item.icon className="nav-link-icon" />}
                    <span className="nav-link-text">{item.label}</span>
                    <FaChevronDown className="nav-link-chevron" aria-hidden />
                  </button>
                ) : item.path === '/' ? (
                  <a
                    href="/"
                    className="nav-link"
                    ref={el => menuItemRefs.current[index] = el}
                    onKeyDown={(e) => handleKeyDown(e, index, false)}
                    onClick={handleNavClick}
                    onFocus={() => setFocusedIndex(index)}
                    aria-current={location.pathname === '/' ? 'page' : undefined}
                  >
                    {item.icon && <item.icon className="nav-link-icon" />}
                    <span className="nav-link-text">{item.label}</span>
                  </a>
                ) : item.openInNewTab ? (
                  <a
                    href={item.path}
                    className="nav-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    ref={el => menuItemRefs.current[index] = el}
                    onKeyDown={(e) => handleKeyDown(e, index, false)}
                    onClick={handleNavClick}
                    onFocus={() => setFocusedIndex(index)}
                    title={item.title || item.label}
                  >
                    {item.icon && <item.icon className="nav-link-icon" />}
                    <span className="nav-link-text">{item.label}</span>
                  </a>
                ) : (
                  <Link 
                    to={item.path} 
                    className="nav-link"
                    ref={el => menuItemRefs.current[index] = el}
                    onKeyDown={(e) => handleKeyDown(e, index, false)}
                    onClick={handleNavClick}
                    onFocus={() => setFocusedIndex(index)}
                    aria-current={location.pathname === item.path ? 'page' : undefined}
                  >
                    {item.icon && <item.icon className="nav-link-icon" />}
                    <span className="nav-link-text">{item.label}</span>
                  </Link>
                )}
                {item.hasSubmenu && (
                  <div
                    className={`mega-menu ${item.label === 'Comunicaciones' ? 'mega-menu--comunicaciones' : ''} ${openDropdown === index ? 'active' : ''}`}
                    onClick={(e) => e.stopPropagation()}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <div className="mega-menu-content">
                      <div className="mega-menu-header">
                        <div className="mega-menu-icon">{item.icon && <item.icon />}</div>
                        <div>
                          <h3 className="mega-menu-title">{item.label}</h3>
                          {item.description && <p className="mega-menu-description">{item.description}</p>}
                        </div>
                      </div>
                      <div className={`mega-menu-grid ${item.submenuGroups ? 'mega-menu-grid-groups' : ''}`}>
                        {item.submenu.map((subItem, subIndex) => {
                          if (subItem.groupTitle) {
                            return (
                              <div key={subIndex} className="mega-menu-group">
                                <div className="mega-menu-group-title">{subItem.groupTitle}</div>
                                <div className="mega-menu-group-items">
                                  {subItem.items.map((it, i) => (
                                    <Link
                                      key={i}
                                      to={it.path}
                                      onClick={handleSubmenuClick}
                                      className="mega-menu-item"
                                      {...(it.openInNewTab || it.path === SIID_PATH
                                        ? { target: '_blank', rel: 'noopener noreferrer' }
                                        : {})}
                                      title={it.title || undefined}
                                    >
                                      {it.icon && <span className="mega-menu-item-icon"><it.icon /></span>}
                                      <span className="mega-menu-item-text">{it.label}</span>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          return (
                            <Link key={subIndex} to={subItem.path} onClick={handleSubmenuClick} className="mega-menu-item" {...(subItem.path === '/search-profile' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                              {subItem.icon && <span className="mega-menu-item-icon"><subItem.icon /></span>}
                              <span className="mega-menu-item-text">{subItem.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </li>
            ))}
            </ul>
          </nav>

          {/* Búsqueda (Donar queda en CTA bajo el hero / página Donaciones) */}
          <div className="header-utils header-utils-floating header-contact">
            <Link to="/busqueda" className="header-util-btn" aria-label="Búsqueda" title="Buscar">
              <FaSearch />
            </Link>
          </div>
        </div>
      </div>
    </header>
    </>
  );
};

export default Header;


